
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react';

interface Step {
  title: string;
  description: string;
  targetId: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

const STEPS: Step[] = [
  {
    title: "Anir-д тавтай морил!",
    description: "Монгол сурагчдад зориулсан нээлттэй боловсролын гүүр ашиглах заавартай танилцана уу.",
    targetId: "hero-section",
    position: "center"
  },
  {
    title: "Өнгө төрхөө өөрчлөх",
    description: "Өөрт тухтай өнгөний горимыг (Өдөр, Сепиа, Шөнө) эндээс сонгоорой.",
    targetId: "theme-changer",
    position: "bottom"
  },
  {
    title: "Номын сан",
    description: "Бүх түвшний сургалтын хөтөлбөр, материалыг эндээс үзэх боломжтой.",
    targetId: "library-button",
    position: "top"
  },
  {
    title: "Бидний амжилт",
    description: "Anir-ын хүрсэн үр дүн болон нийгэмд үзүүлж буй нөлөөллийг эндээс хараарай.",
    targetId: "impact-section",
    position: "top"
  },
  {
    title: "Өдрийн үг",
    description: "Монгол бичгийн мэдлэгээ өдөр бүр нэг үгээр баяжуулаарай.",
    targetId: "word-of-the-day",
    position: "left"
  },
  {
    title: "Багшлах ба Тусламж авах",
    description: "Өөрийн мэддэг зүйлээрээ бусдад тусалж багш болох, эсвэл үе тэнгийнхнээсээ тусламж хүсэх боломжтой.",
    targetId: "tutor-apply-nav",
    position: "right"
  },
  {
    title: "Нэвтрэх",
    description: "Өөрийн суралцах явцаа хянахын тулд бүртгүүлж эсвэл нэвтрээрэй.",
    targetId: "auth-button",
    position: "bottom"
  }
];

const SiteGuide: React.FC<{ currentView: string }> = ({ currentView }) => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [isVisible, setIsVisible] = useState(false);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('anir_guide_seen');
    if (!hasSeenGuide && currentView === 'home') {
      setTimeout(() => {
        setIsVisible(true);
        setCurrentStep(0);
      }, 1500);
    }
  }, [currentView]);

  useEffect(() => {
    const updateRect = () => {
      if (currentStep >= 0 && currentStep < STEPS.length) {
        const step = STEPS[currentStep];
        const element = document.getElementById(step.targetId);
        if (element) {
          if (step.targetId !== 'hero-section') {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          setTimeout(() => {
            setTargetRect(element.getBoundingClientRect());
          }, 500);
        } else {
          setTargetRect(null);
        }
      }
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('anir_guide_seen', 'true');
  };

  if (!isVisible || currentStep === -1) return null;

  const step = STEPS[currentStep];

  const getPopupStyles = () => {
    if (!targetRect || step.targetId === 'hero-section') {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }

    const padding = 20;
    switch (step.position) {
      case 'bottom':
        return { top: targetRect.bottom + padding, left: targetRect.left + targetRect.width / 2, transform: 'translateX(-50%)' };
      case 'top':
        return { top: targetRect.top - padding, left: targetRect.left + targetRect.width / 2, transform: 'translate(-50%, -100%)' };
      case 'left':
        return { top: targetRect.top + targetRect.height / 2, left: targetRect.left - padding, transform: 'translate(-100%, -50%)' };
      case 'right':
        return { top: targetRect.top + targetRect.height / 2, left: targetRect.right + padding, transform: 'translate(0, -50%)' };
      default:
        return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
  };

  return (
    <div className="fixed inset-0 z-[200] pointer-events-none">
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/20 backdrop-blur-[1px]"
          />
        )}
      </AnimatePresence>

      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.9, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 10 }}
          style={getPopupStyles()}
          className="absolute bg-white text-slate-900 p-6 rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.2)] w-72 pointer-events-auto border border-slate-100 space-y-4"
        >
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[8px] font-bold uppercase tracking-widest text-primary bg-primary/5 px-2 py-1 rounded-full">
                {currentStep + 1} / {STEPS.length}
              </span>
              <button onClick={handleClose} className="text-slate-400 hover:text-slate-900 transition-colors">
                <X size={14} />
              </button>
            </div>
            <h3 className="text-lg font-serif tracking-tight">{step.title}</h3>
            <p className="text-[11px] font-medium text-slate-500 leading-relaxed">
              {step.description}
            </p>
          </div>

          <div className="flex justify-between items-center pt-2">
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className={`text-[9px] font-bold uppercase tracking-widest ${currentStep === 0 ? 'opacity-20' : 'hover:text-primary'}`}
            >
              Өмнөх
            </button>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="text-[9px] font-bold uppercase tracking-widest text-slate-400 hover:text-slate-900"
              >
                Алгасах
              </button>
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-slate-900 text-white rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-primary transition-all shadow-lg"
              >
                {currentStep === STEPS.length - 1 ? 'Дуусгах' : 'Дараах'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SiteGuide;
